import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { bookingAPI, messageAPI, reviewAPI, studentAPI, chatAPI, ordersAPI, studentHoursAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Calendar, 
  Clock, 
  Video,
  MessageSquare,
  Star,
  BookOpen,
  Loader2,
  LogOut,
  ExternalLink,
  Plus,
  User,
  School,
  Edit2,
  Save,
  X,
  Send,
  MessageCircle,
  Headphones,
  DollarSign,
  Package,
  CheckCircle,
  AlertCircle,
  CreditCard,
  AlertTriangle,
  ShieldCheck,
  Mail,
  FileText
} from 'lucide-react';

const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Student profile state
  const [studentProfile, setStudentProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    student_grade: '',
    school_name: '',
    current_courses: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Live Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const chatContainerRef = useRef(null);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [confirmingSent, setConfirmingSent] = useState(null);

  // Hours tracking state
  const [hoursData, setHoursData] = useState(null);
  const [loadingHours, setLoadingHours] = useState(false);

  // Booking eligibility state
  const [bookingEligibility, setBookingEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(true);

  // Helper function to format session dates consistently (avoids timezone shift)
  const formatSessionDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse as local date to avoid timezone conversion
    // Date string is in YYYY-MM-DD format from backend
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
    }
    return dateString;
  };

  const gradeOptions = [
    { value: 'grade-9', label: 'Grade 9' },
    { value: 'grade-10', label: 'Grade 10' },
    { value: 'grade-11', label: 'Grade 11' },
    { value: 'grade-12', label: 'Grade 12' },
    { value: 'university-1', label: 'University - Year 1' },
    { value: 'university-2', label: 'University - Year 2' },
    { value: 'university-3', label: 'University - Year 3' },
    { value: 'university-4', label: 'University - Year 4' },
    { value: 'graduate', label: 'Graduate Student' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    loadData();
    loadChatMessages();
    loadHoursData();
    loadBookingEligibility();
  }, []);

  // Poll for new chat messages every 10 seconds when chat is open
  useEffect(() => {
    if (chatOpen) {
      const interval = setInterval(loadChatMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [chatOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current && chatOpen) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, chatOpen]);

  const loadData = async () => {
    try {
      const [bookingsRes, messagesRes, profileRes, ordersRes] = await Promise.all([
        bookingAPI.list(),
        messageAPI.listConversations(),
        studentAPI.getProfile().catch(() => ({ data: null })),
        ordersAPI.getMyOrders().catch(() => ({ data: { orders: [] } }))
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setConversations(messagesRes.data.conversations || []);
      setOrders(ordersRes.data.orders || []);
      
      if (profileRes.data) {
        setStudentProfile(profileRes.data);
        setProfileForm({
          student_grade: profileRes.data.student_grade || '',
          school_name: profileRes.data.school_name || '',
          current_courses: (profileRes.data.current_courses || []).join(', ')
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await ordersAPI.getMyOrders();
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleConfirmEtransferSent = async (orderId) => {
    setConfirmingSent(orderId);
    try {
      await ordersAPI.confirmSent(orderId);
      toast.success('Confirmation sent! We will verify your payment shortly.');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirmingSent(null);
    }
  };

  const loadChatMessages = async () => {
    try {
      const response = await chatAPI.getMessages({ limit: 100 });
      setChatMessages(response.data.messages || []);
      setUnreadChatCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  };

  const loadHoursData = async () => {
    setLoadingHours(true);
    try {
      const response = await studentHoursAPI.getMyHours();
      setHoursData(response.data);
    } catch (error) {
      console.error('Error loading hours data:', error);
    } finally {
      setLoadingHours(false);
    }
  };

  const loadBookingEligibility = async () => {
    setLoadingEligibility(true);
    try {
      const response = await studentAPI.getBookingEligibility();
      setBookingEligibility(response.data);
    } catch (error) {
      console.error('Error loading booking eligibility:', error);
      // Default to eligible if API fails (graceful degradation)
      setBookingEligibility({ eligible: true, issues: [] });
    } finally {
      setLoadingEligibility(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await chatAPI.sendMessage({ message: newMessage.trim() });
      setNewMessage('');
      await loadChatMessages();
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const WHATSAPP_LINK = "https://wa.me/qr/M6WGQZJHE55AN1";
  
  const handleOpenChat = () => {
    // Open WhatsApp chat directly
    window.open(WHATSAPP_LINK, '_blank', 'noopener,noreferrer');
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const coursesArray = profileForm.current_courses
        .split(',')
        .map(c => c.trim())
        .filter(c => c.length > 0);

      await studentAPI.updateProfile({
        student_grade: profileForm.student_grade || null,
        school_name: profileForm.school_name || null,
        current_courses: coursesArray
      });

      toast.success('Profile updated successfully!');
      setEditingProfile(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const getGradeLabel = (value) => {
    const option = gradeOptions.find(o => o.value === value);
    return option ? option.label : value || 'Not set';
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await bookingAPI.cancel(bookingId);
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedBooking) return;
    
    setSubmittingReview(true);
    try {
      await reviewAPI.create({
        tutor_id: selectedBooking.tutor_id,
        booking_id: selectedBooking.booking_id,
        rating: reviewData.rating,
        comment: reviewData.comment
      });
      toast.success('Review submitted!');
      setReviewOpen(false);
      setReviewData({ rating: 5, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const upcomingBookings = bookings.filter(b => 
    ['pending', 'confirmed'].includes(b.status) && new Date(b.date) >= new Date()
  );
  const pastBookings = bookings.filter(b => 
    b.status === 'completed' || new Date(b.date) < new Date()
  );

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-green-100 text-green-700',
    completed: 'bg-slate-100 text-slate-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="student-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white p-6 hidden lg:block">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <GraduationCap className="h-8 w-8 text-indigo-400" />
          <span className="font-heading text-lg font-semibold">Gifted Academy Tutors</span>
        </Link>

        <nav className="space-y-2">
          <div className="px-4 py-2 bg-slate-800 rounded-lg text-white flex items-center gap-3">
            <Calendar className="h-5 w-5" />
            <span>My Sessions</span>
          </div>
          <Link 
            to="/messages" 
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white flex items-center gap-3 transition-colors"
          >
            <MessageSquare className="h-5 w-5" />
            <span>Messages</span>
            {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0) > 0 && (
              <Badge className="ml-auto bg-indigo-600 text-white">
                {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0)}
              </Badge>
            )}
          </Link>
          <Link 
            to="/tutors" 
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white flex items-center gap-3 transition-colors"
          >
            <BookOpen className="h-5 w-5" />
            <span>Find Tutors</span>
          </Link>
          <button 
            onClick={handleOpenChat}
            className="w-full px-4 py-2 rounded-lg text-slate-400 hover:bg-green-600 hover:text-white flex items-center gap-3 transition-colors"
            data-testid="live-chat-btn"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>Chat on WhatsApp</span>
          </button>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-indigo-600">{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-stone-100 p-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-indigo-700" />
              <span className="font-heading text-lg font-semibold">Dashboard</span>
            </Link>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-semibold text-slate-900">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">
              Here's an overview of your tutoring sessions.
            </p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <Card className="border-stone-100">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{hoursData?.recent_sessions?.length || 0}</p>
                    <p className="text-sm text-slate-500">Completed Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-stone-100">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-amber-700" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{conversations.length}</p>
                    <p className="text-sm text-slate-500">Conversations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tutoring Hours Card */}
          {hoursData && (hoursData.total_hours_purchased > 0 || hoursData.total_hours_used > 0) && (
            <Card className="border-stone-100 mb-8" data-testid="tutoring-hours-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Clock className="h-5 w-5 text-indigo-700" />
                  My Tutoring Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                    <p className="text-sm text-blue-600 mb-1">Purchased</p>
                    <p className="text-2xl font-bold text-blue-700">{hoursData.total_hours_purchased || 0}</p>
                    <p className="text-xs text-blue-500">hours</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-100">
                    <p className="text-sm text-amber-600 mb-1">Used</p>
                    <p className="text-2xl font-bold text-amber-700">{hoursData.total_hours_used || 0}</p>
                    <p className="text-xs text-amber-500">hours</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                    <p className="text-sm text-green-600 mb-1">Remaining</p>
                    <p className="text-2xl font-bold text-green-700">{hoursData.total_hours_remaining || 0}</p>
                    <p className="text-xs text-green-500">hours</p>
                  </div>
                </div>

                {hoursData.total_hours_remaining <= 2 && hoursData.total_hours_remaining > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <p className="text-sm text-amber-800">
                      Low hours balance! Consider purchasing more tutoring hours.
                    </p>
                  </div>
                )}

                {hoursData.recent_sessions?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Sessions</h4>
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {hoursData.recent_sessions.slice(0, 5).map((session) => (
                        <div 
                          key={session.log_id}
                          className="bg-slate-50 rounded-lg p-3 border"
                          data-testid={`session-${session.log_id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-900">{formatSessionDate(session.session_date)}</p>
                              {session.session_time && (
                                <span className="text-sm text-slate-500">at {session.session_time}</span>
                              )}
                            </div>
                            <Badge className="bg-indigo-100 text-indigo-700">
                              {session.hours_used} hrs
                            </Badge>
                          </div>
                          {session.tutor_name && (
                            <p className="text-sm text-slate-600">
                              <span className="text-slate-500">Tutor:</span> {session.tutor_name}
                            </p>
                          )}
                          {session.notes && (
                            <p className="text-xs text-slate-500 mt-1">{session.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Student Profile Section */}
          <Card className="border-stone-100 mb-8" data-testid="student-profile-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-700" />
                My Profile
              </CardTitle>
              {!editingProfile ? (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full gap-2"
                  onClick={() => setEditingProfile(true)}
                  data-testid="edit-profile-btn"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setEditingProfile(false);
                      if (studentProfile) {
                        setProfileForm({
                          student_grade: studentProfile.student_grade || '',
                          school_name: studentProfile.school_name || '',
                          current_courses: (studentProfile.current_courses || []).join(', ')
                        });
                      }
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    className="rounded-full gap-2 bg-indigo-700 hover:bg-indigo-800"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    data-testid="save-profile-btn"
                  >
                    {savingProfile ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {editingProfile ? (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="student_grade">Current Grade/Year</Label>
                      <Select 
                        value={profileForm.student_grade} 
                        onValueChange={(value) => setProfileForm(prev => ({ ...prev, student_grade: value }))}
                      >
                        <SelectTrigger data-testid="edit-grade-select">
                          <SelectValue placeholder="Select your grade/year" />
                        </SelectTrigger>
                        <SelectContent>
                          {gradeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="school_name">School Name</Label>
                      <Input
                        id="school_name"
                        value={profileForm.school_name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, school_name: e.target.value }))}
                        placeholder="Enter your school name"
                        data-testid="edit-school-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="current_courses">Current Courses</Label>
                    <Input
                      id="current_courses"
                      value={profileForm.current_courses}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, current_courses: e.target.value }))}
                      placeholder="e.g., Math, Physics, Chemistry (comma separated)"
                      data-testid="edit-courses-input"
                    />
                    <p className="text-xs text-slate-500">Separate multiple courses with commas</p>
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="h-5 w-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Grade/Year</p>
                      <p className="font-medium text-slate-900" data-testid="profile-grade">
                        {getGradeLabel(studentProfile?.student_grade)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <School className="h-5 w-5 text-purple-700" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">School</p>
                      <p className="font-medium text-slate-900" data-testid="profile-school">
                        {studentProfile?.school_name || 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="h-5 w-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Current Courses</p>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="profile-courses">
                        {studentProfile?.current_courses?.length > 0 ? (
                          studentProfile.current_courses.map((course, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {course}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-500">Not set</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sessions */}
          <Card className="border-stone-100">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <CardTitle className="font-heading text-xl">My Sessions</CardTitle>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contact')} 
                  className="rounded-full gap-2"
                  data-testid="contact-admin-btn"
                >
                  <MessageCircle className="h-4 w-4" />
                  Contact Admin
                </Button>
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="rounded-full gap-2"
                  data-testid="add-hours-btn"
                >
                  <Plus className="h-4 w-4" />
                  Add More Hours
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              <Tabs defaultValue="orders">
                <TabsList className="mb-4">
                  <TabsTrigger value="orders" data-testid="orders-tab">
                    My Orders ({orders.length})
                  </TabsTrigger>
                  <TabsTrigger value="past" data-testid="past-tab">
                    Completed ({hoursData?.recent_sessions?.length || 0})
                  </TabsTrigger>
                </TabsList>

                {/* Orders Tab */}
                <TabsContent value="orders">
                  {loadingOrders ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-indigo-700" />
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-heading text-lg text-slate-900 mb-2">No orders yet</h3>
                      <p className="text-slate-500 mb-4">Purchase a tutoring package to get started.</p>
                      <Button onClick={() => navigate('/pricing')} className="rounded-full">
                        <CreditCard className="h-4 w-4 mr-2" />
                        View Packages
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <motion.div
                          key={order.order_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`p-4 rounded-xl border ${
                            order.payment_status === 'paid' ? 'bg-green-50 border-green-200' :
                            order.payment_status === 'sent' ? 'bg-blue-50 border-blue-200' :
                            'bg-amber-50 border-amber-200'
                          }`}
                          data-testid={`order-${order.order_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Package className={`h-5 w-5 ${
                                  order.payment_status === 'paid' ? 'text-green-600' :
                                  order.payment_status === 'sent' ? 'text-blue-600' :
                                  'text-amber-600'
                                }`} />
                                <span className="font-semibold text-slate-900">{order.package_name}</span>
                                <Badge className={
                                  order.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                  order.payment_status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                  'bg-amber-100 text-amber-700'
                                }>
                                  {order.payment_status === 'paid' ? 'Paid' :
                                   order.payment_status === 'sent' ? 'Awaiting Verification' :
                                   'Awaiting E-Transfer'}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-3">
                                <div>
                                  <span className="text-slate-500">Order ID:</span>{' '}
                                  <span className="font-mono font-medium">{order.order_id}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Amount:</span>{' '}
                                  <span className="font-semibold">${order.amount}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Hours:</span>{' '}
                                  <span className="font-medium">{order.hours} hours</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Date:</span>{' '}
                                  <span>{order.created_at ? format(new Date(order.created_at), 'MMM d, yyyy') : 'N/A'}</span>
                                </div>
                              </div>

                              <div className="text-sm">
                                <span className="text-slate-500">Subjects:</span>{' '}
                                <span>{order.subjects?.join(', ') || 'N/A'}</span>
                              </div>

                              {order.payment_status === 'paid' && (
                                <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                                  <div className="flex items-center gap-2 text-green-700">
                                    <CheckCircle className="h-4 w-4" />
                                    <span className="font-medium">Payment Confirmed</span>
                                  </div>
                                  <p className="text-sm text-slate-600 mt-1">
                                    Your tutor will be assigned shortly. We'll notify you once matched.
                                  </p>
                                </div>
                              )}

                              {order.payment_status === 'sent' && (
                                <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                                  <div className="flex items-center gap-2 text-blue-700">
                                    <Clock className="h-4 w-4" />
                                    <span className="font-medium">Verification in Progress</span>
                                  </div>
                                  <p className="text-sm text-slate-600 mt-1">
                                    We're verifying your e-transfer. This usually takes 24 hours.
                                  </p>
                                </div>
                              )}

                              {order.payment_status === 'pending' && (
                                <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                                  <div className="flex items-center gap-2 text-amber-700 mb-2">
                                    <AlertCircle className="h-4 w-4" />
                                    <span className="font-medium">E-Transfer Required</span>
                                  </div>
                                  <p className="text-sm text-slate-600 mb-3">
                                    Send <strong>${order.amount}</strong> via Interac e-Transfer to:{' '}
                                    <span className="font-mono text-indigo-700">contact@giftedacademytutors.ca</span>
                                  </p>
                                  <p className="text-xs text-slate-500 mb-3">
                                    Include your order reference ({order.order_id}) in the message.
                                  </p>
                                  <Button
                                    onClick={() => handleConfirmEtransferSent(order.order_id)}
                                    disabled={confirmingSent === order.order_id}
                                    className="rounded-full bg-blue-600 hover:bg-blue-700"
                                    size="sm"
                                    data-testid={`confirm-sent-${order.order_id}`}
                                  >
                                    {confirmingSent === order.order_id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Confirming...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        I've Sent the E-Transfer
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="past">
                  {(!hoursData?.recent_sessions || hoursData.recent_sessions.length === 0) ? (
                    <div className="text-center py-12">
                      <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-heading text-lg text-slate-900">No completed sessions</h3>
                      <p className="text-slate-500">Your tutor-reported sessions will appear here.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {hoursData.recent_sessions.map((session, index) => (
                        <div
                          key={session.log_id || index}
                          className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                          data-testid={`session-${session.log_id || index}`}
                        >
                          <div className="flex items-center gap-4">
                            <Avatar className="h-12 w-12">
                              <AvatarFallback className="bg-green-100 text-green-700">
                                {session.tutor_name?.charAt(0) || 'T'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-semibold text-slate-900">{session.tutor_name || 'Tutor'}</h4>
                              <p className="text-sm text-slate-600">{session.lesson_topic || 'Session'}</p>
                              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {formatSessionDate(session.session_date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {session.hours_used} hr{session.hours_used !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className="bg-green-100 text-green-700">
                              Completed
                            </Badge>
                            {session.lesson_notes_url && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="rounded-full gap-2"
                                onClick={() => window.open(session.lesson_notes_url, '_blank')}
                                data-testid={`lesson-notes-${session.log_id || index}`}
                              >
                                <FileText className="h-4 w-4" />
                                Lesson Notes
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Live Chat Dialog */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col" data-testid="chat-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="h-5 w-5 text-indigo-700" />
              Live Chat with Admin
            </DialogTitle>
          </DialogHeader>
          
          {/* Messages Container */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto space-y-3 p-4 bg-stone-50 rounded-lg min-h-[300px] max-h-[400px]"
            data-testid="chat-messages-container"
          >
            {chatMessages.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No messages yet</p>
                <p className="text-sm">Send a message to start chatting with admin</p>
              </div>
            ) : (
              chatMessages.map((msg) => (
                <div 
                  key={msg.message_id}
                  className={`flex ${msg.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`}
                >
                  <div 
                    className={`max-w-[80%] rounded-lg px-4 py-2 ${
                      msg.sender_type === 'admin' 
                        ? 'bg-white border border-stone-200' 
                        : 'bg-indigo-600 text-white'
                    }`}
                    data-testid={`chat-message-${msg.message_id}`}
                  >
                    {msg.sender_type === 'admin' && (
                      <p className="text-xs font-medium text-indigo-700 mb-1">
                        {msg.sender_name}
                      </p>
                    )}
                    <p className={`text-sm ${msg.sender_type === 'admin' ? 'text-slate-700' : 'text-white'}`}>
                      {msg.message}
                    </p>
                    <p className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-slate-400' : 'text-indigo-200'}`}>
                      {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Message Input */}
          <div className="flex gap-2 pt-2 border-t border-stone-100">
            <Input
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              disabled={sendingMessage}
              data-testid="chat-input"
            />
            <Button 
              onClick={handleSendMessage}
              disabled={sendingMessage || !newMessage.trim()}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="chat-send-btn"
            >
              {sendingMessage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          
          <p className="text-xs text-center text-slate-400 mt-2">
            Messages are stored and admin will respond as soon as possible
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDashboard;
